// =============================================================================
// invertedQr.js — read a customer's QR when their phone has flipped it to
// cream-on-dark.
//
// Why (Student Night, 1 Oct 2026): some phones in dark mode recolour the
// customer app, QR image included. Samsung Internet does it, and so do in-app
// browsers (a link opened from Instagram, say) that darken pages themselves.
// The code comes out INVERTED: light squares on a dark background. html5-qrcode
// (ZXing) only reads dark-on-light, so the till never saw it. The app now draws
// the QR's light border into the image (rolled-frontend #303), but that can't
// stop a browser that inverts the whole image.
//
// So alongside html5-qrcode, a few times a second, we take the camera frame it
// is already showing, flip it, and read it with jsQR. A hit goes through the
// scanner's normal handler, exactly as if html5-qrcode had read it. Normal
// codes are left to html5-qrcode, so nothing is read twice.
// =============================================================================
import jsQR from "jsqr";

// Text of an inverted QR in an RGBA frame, or null. Only tries the flipped
// picture: a normal dark-on-light code is html5-qrcode's job. We flip the
// pixels ourselves because jsQR 1.4.0's own inversionAttempts "onlyInvert"
// crashes (it never builds the inverted image it then reads).
export function readInvertedQr(rgba, width, height) {
  const flipped = new Uint8ClampedArray(rgba.length);
  for (let i = 0; i < rgba.length; i += 4) {
    flipped[i] = 255 - rgba[i];
    flipped[i + 1] = 255 - rgba[i + 1];
    flipped[i + 2] = 255 - rgba[i + 2];
    flipped[i + 3] = 255;
  }
  const code = jsQR(flipped, width, height, { inversionAttempts: "dontInvert" });
  return code && code.data ? code.data : null;
}

// Starts the side reader for the html5-qrcode reader in #<readerId>. Returns a
// stop function.
//   isScanning() — true while html5-qrcode is scanning. The reader only looks
//                  then, so a stopped or paused camera is never re-read.
//   onDecode(t)  — the scanner's own success handler.
// Reads the centre square of the frame (where the scan box sits), scaled down
// to at most `maxSide` px so a frame takes tens of milliseconds, not hundreds.
export function startInvertedQrFallback({ readerId, isScanning, onDecode, everyMs = 300, maxSide = 480 }) {
  let stopped = false;
  let timer = null;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const tick = () => {
    if (stopped) return;
    try {
      const video = document.querySelector("#" + readerId + " video");
      if (ctx && video && isScanning() && !video.paused && video.readyState >= 2 && video.videoWidth) {
        const vw = video.videoWidth, vh = video.videoHeight;
        const side = Math.min(vw, vh);
        const size = Math.min(side, maxSide);
        if (canvas.width !== size) { canvas.width = size; canvas.height = size; }
        ctx.drawImage(video, (vw - side) / 2, (vh - side) / 2, side, side, 0, 0, size, size);
        const text = readInvertedQr(ctx.getImageData(0, 0, size, size).data, size, size);
        if (text && !stopped) onDecode(text);
      }
    } catch (e) {
      // A frame we couldn't read (camera mid-restart, say): try the next one.
    }
    if (!stopped) timer = setTimeout(tick, everyMs);
  };
  timer = setTimeout(tick, everyMs);
  return () => { stopped = true; clearTimeout(timer); };
}
